import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LANGUAGES } from "@/lib/i18n";
import { toast } from "sonner";
import { Loader2, Save, User, Building2, Languages, MessageSquare, KeyRound, Cpu, Activity } from "lucide-react";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-violet-500/30";
const CONV_LANGS = [{ code: "az", label: "Azərbaycan dili" }, { code: "en", label: "English" }, { code: "tr", label: "Türkçe" }, { code: "ru", label: "Русский" }];

function Card({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-6" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center"><Icon className="w-4 h-4 text-violet-300" /></div>
        <h3 className="text-lg font-display font-semibold text-slate-100">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function Settings() {
  const { settings, loadSettings } = useStudio();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [llm, setLlm] = useState(null);

  useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);
  useEffect(() => { api.get("/settings/llm-usage").then(r => setLlm(r.data)).catch(() => {}); }, []);

  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!form) return <LoadingState label="Loading settings…" />;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        workspace_name: form.workspace_name, interface_language: form.interface_language,
        conversation_language_default: form.conversation_language_default, owner_name: form.owner_name,
        default_output_language: form.default_output_language || "en-US",
        production_monitor_enabled: form.production_monitor_enabled !== false,
      });
      await Promise.all([loadSettings(), refresh()]);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (e) { toast.error(apiError(e)); } finally { setPwBusy(false); }
  };

  return (
    <div className="animate-in-up max-w-3xl">
      <div className="mb-8">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-violet-400 mb-1">Workspace</p>
        <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Settings</h1>
      </div>

      <div className="space-y-5">
        <Card icon={User} title="Owner profile" testid="settings-owner">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Name</Label>
              <Input className={inputCls} value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="settings-owner-name" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Email</Label>
              <Input className={`${inputCls} opacity-60`} value={user?.email || ""} disabled data-testid="settings-owner-email" /></div>
          </div>
        </Card>

        <Card icon={KeyRound} title="Change password" testid="settings-password">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div><Label className="text-slate-300 text-sm mb-1.5 block">Current password</Label>
              <Input type="password" className={inputCls} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" /></div>
            <div><Label className="text-slate-300 text-sm mb-1.5 block">New password</Label>
              <Input type="password" className={inputCls} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" /></div>
          </div>
          <Button onClick={changePassword} disabled={pwBusy} className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="change-password-button">
            {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </Button>
        </Card>

        <Card icon={Building2} title="Workspace" testid="settings-workspace">
          <Label className="text-slate-300 text-sm mb-1.5 block">Workspace name</Label>
          <Input className={inputCls} value={form.workspace_name} onChange={(e) => setForm({ ...form, workspace_name: e.target.value })} data-testid="settings-workspace-name" />
        </Card>

        <Card icon={Languages} title="Language" testid="settings-language">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block">Interface language</Label>
              <Select value={form.interface_language} onValueChange={(v) => setForm({ ...form, interface_language: v })}>
                <SelectTrigger className={inputCls} data-testid="settings-interface-language"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">Changes navigation &amp; top bar labels immediately after save.</p>
            </div>
            <div>
              <Label className="text-slate-300 text-sm mb-1.5 block flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Conversation default</Label>
              <Select value={form.conversation_language_default} onValueChange={(v) => setForm({ ...form, conversation_language_default: v })}>
                <SelectTrigger className={inputCls} data-testid="settings-conversation-language"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">{CONV_LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1.5">Used by the AI Command Center (Phase 2).</p>
            </div>
          </div>
        </Card>

        <Card icon={Cpu} title="LLM usage" testid="settings-llm-usage">
          {!llm ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Serving provider</span>
                <span className="text-slate-100 font-medium" data-testid="llm-provider">{llm.active_provider}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Key (masked)</span>
                <span className="font-mono text-violet-300" data-testid="llm-key-alias">{llm.key_alias_masked}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Calls this month ({llm.month})</span>
                <span className="text-slate-100" data-testid="llm-calls">{llm.calls_this_month}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Estimated cost this month</span>
                <span className="text-slate-100" data-testid="llm-cost">{llm.cost_known ? `$${llm.cost_this_month}` : "unknown"}</span>
              </div>
              <p className="text-[11px] text-slate-500 pt-1">Read-only. Raw keys are never shown. Billed via the built-in Emergent universal key unless you connect your own provider.</p>
            </div>
          )}
        </Card>

        <Card icon={Activity} title="Production Monitor" testid="settings-monitor">
          <div className="flex items-center justify-between">
            <div className="pr-4">
              <p className="text-sm text-slate-200">Show the Production Monitor section</p>
              <p className="text-xs text-slate-500 mt-0.5">Live workflow map + run history for real autopilot, render and goal runs. When off, the nav item and route are hidden and its API returns 404.</p>
            </div>
            <button type="button" onClick={() => setForm({ ...form, production_monitor_enabled: !(form.production_monitor_enabled !== false) })}
              className={`relative w-12 h-6 rounded-full transition-colors flex-none ${form.production_monitor_enabled !== false ? "bg-violet-600" : "bg-slate-700"}`}
              data-testid="settings-monitor-toggle" aria-pressed={form.production_monitor_enabled !== false}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.production_monitor_enabled !== false ? "translate-x-6" : ""}`} />
            </button>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="save-settings-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save settings</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
